import { generateSectionId } from '../../../HelpCenterComponents/ArticleLayout';

export const ContentBody = ({ activeLeft }) => {
  const contentData = {
    0: {
      intro:
        'Spatial Grid is an enterprise-grade spatial computing platform designed to build, deploy, and scale immersive AR, VR, simulator and MR experiences across industries. This section introduces the platform’s philosophy, core capabilities, and how enterprises use Spatial Grid to drive training, visualization, and engagement.',
      sections: [
        {
          type: 'text',
          heading: 'What is Spatial Grid',
          paragraphs: [
            'Spatial Grid is a comprehensive spatial computing platform purpose-built for enterprises to design, deploy, and scale immersive AR, VR, and MR experiences across complex real-world environments. It enables organizations to transform physical processes, assets, and knowledge into interactive spatial experiences that can be accessed across devices and locations.',
            'Unlike consumer-focused XR tools built for individual creators or short-term engagement, Spatial Grid is engineered for long-term operational use. It supports enterprise-grade governance, security, performance, and scalability, making it suitable for mission-critical training, simulations, visualization, and engagement initiatives.',
            'Spatial Grid serves as a foundational layer for spatial transformation—allowing enterprises to move beyond experimentation and embed immersive technologies into everyday workflows, learning systems, and operational decision-making.',
          ],
        },
        {
          type: 'text',
          heading: 'Platform Philosophy',
          paragraphs: [
            'At the heart of Spatial Grid is a philosophy centered on performance, scalability, and adaptability. The platform is designed to handle complex spatial data, real-time interactions, and high-fidelity experiences without compromising system efficiency or stability.',
            'Spatial Grid is built with future readiness in mind. As hardware, interaction paradigms, and AI capabilities evolve, the platform is structured to absorb these changes without forcing enterprises to rebuild or re-architect existing experiences. This protects long-term investments and ensures continuity as technologies advance.',
            'Equally important is the platform’s enterprise-first mindset. Spatial Grid emphasizes reliability, maintainability, and governance—recognizing that immersive systems must operate within real business constraints, IT policies, and organizational structures.',
          ],
        },
        {
          type: 'text',
          heading: 'Core Capabilities',
          paragraphs: [
            'Spatial Grid brings together the essential building blocks required for enterprise spatial computing into a unified platform. These include spatial visualization engines, real-time interaction systems, AI-driven behaviors, analytics, and cross-device deployment frameworks.',
            'The platform supports complex environment logic, intelligent interactions, guided workflows, and adaptive scenarios—enabling experiences that respond dynamically to user behavior and context. Built-in analytics capture meaningful interaction data, allowing enterprises to measure engagement, performance, and outcomes.',
            'A key capability of Spatial Grid is its ability to abstract hardware complexity. Experiences can be developed once and deployed across AR, VR, MR, mobile, simulators, and large-format displays—significantly reducing development effort and operational overhead.',
            'Together, these capabilities allow organizations to create immersive experiences that are visually compelling, operationally robust, and aligned with measurable business objectives.',
          ],
        },
        {
          type: 'text',
          heading: 'Enterprise Value',
          paragraphs: [
            'Enterprises adopt Spatial Grid to address real-world challenges where traditional tools fall short. By enabling immersive, hands-on learning and visualization, organizations reduce operational risk, improve safety, and enhance workforce readiness.',
            'Spatial Grid accelerates understanding of complex systems, processes, and environments—helping users move from passive consumption to active engagement. This leads to improved retention, better decision-making, and higher confidence in real-world execution.',
            'From a business perspective, Spatial Grid delivers value through lower total cost of ownership, faster deployment cycles, and future-proof technology adoption. Organizations gain a scalable spatial foundation that supports long-term initiatives across training, operations, marketing, and research.',
            'Ultimately, Spatial Grid empowers enterprises to turn spatial experiences into strategic assets—driving measurable outcomes, sustained engagement, and competitive advantage.',
          ],
        },
      ],
    },
    1: {
      intro:
        'Learn how organizations apply Spatial Grid across manufacturing, pharma, mining, defence, real estate, tourism, and education. Explore common use cases such as immersive training, simulations, digital twins, spatial visualization, and interactive marketing experiences.',
      sections: [
        {
          type: 'text',
          heading: 'Understanding Spatial Grid Use Cases',
          paragraphs: [
            'Spatial Grid is designed to support a wide range of enterprise and institutional use cases where spatial understanding, hands-on interaction, and real-world context are critical. The platform enables organizations to move beyond static content and traditional interfaces by embedding learning, operations, and engagement directly into immersive spatial environments. These use cases span training, simulation, visualization, engagement, and research—each tailored to enterprise-scale needs.',
          ],
        },
        {
          type: 'text',
          heading: 'Immersive Training & SOP Enablement',
          paragraphs: [
            'Spatial Grid enables organizations to transform standard operating procedures into immersive, scenario-based training experiences. Instead of relying solely on manuals, videos, or classroom instruction, workers can practice procedures within realistic virtual environments that closely mirror real-world conditions.',
            'This approach allows personnel to safely experience hazardous, complex, or rare scenarios without operational risk. Training can be standardized across locations while still adapting to local conditions, equipment, and regulatory requirements. By building muscle memory and situational awareness, enterprises improve compliance, reduce errors, and enhance overall workforce readiness.',
          ],
        },
        {
          type: 'text',
          heading: 'Simulation & Operational Readiness',
          paragraphs: [
            'For operations involving complex systems, equipment, or high-risk scenarios, Spatial Grid provides advanced simulation capabilities. Teams can rehearse procedures, emergency responses, and operational workflows in controlled environments before executing them in the real world.',
            'Simulations can model machinery behavior, vehicle operation, defense systems, or mission-critical processes—allowing users to test decision-making, coordination, and response strategies. This leads to improved preparedness, reduced downtime, and increased confidence when transitioning from simulation to live operations.',
          ],
        },
        {
          type: 'text',
          heading: 'Digital Twins & Spatial Visualization',
          paragraphs: [
            'Spatial Grid enables enterprises to create digital twins and spatial representations of physical assets, facilities, or geographic environments. These digital models provide a three-dimensional view of real-world systems, helping stakeholders understand spatial relationships, workflows, and dependencies.',
            'Organizations use spatial visualization to support planning, walkthroughs, layout optimization, and process analysis. By layering data, context, and interactivity onto physical environments, Spatial Grid helps teams make better-informed decisions and communicate complex information more effectively.',
          ],
        },
        {
          type: 'text',
          heading: 'Interactive Marketing & Engagement',
          paragraphs: [
            'Spatial Grid supports immersive, AR-led marketing and engagement experiences that bridge physical and digital touchpoints. Products, spaces, and stories can be brought to life through interactive visualization, guided narratives, and location-aware experiences.',
            'Enterprises deploy these experiences across print, digital, retail, outdoor, and on-site environments—enabling customers to explore products, understand features, and engage with brands in more meaningful ways. This results in higher engagement, improved recall, and stronger connections between audiences and offerings.',
          ],
        },
        {
          type: 'text',
          heading: 'Education & Research',
          paragraphs: [
            'Educational institutions and research organizations use Spatial Grid to create immersive learning environments that encourage exploration and experiential understanding. Complex concepts, systems, and environments can be visualized and interacted with rather than simply described.',
            'Spatial Grid supports simulations, experiments, and exploratory experiences across disciplines—enhancing comprehension, retention, and curiosity. For research and outreach initiatives, the platform enables interactive demonstrations that make advanced topics accessible to wider audiences, including students, stakeholders, and the public.',
          ],
        },
      ],
    },
    2: {
      intro:
        'Get an overview of Spatial Grid’s modular architecture, including spatial engines, AI plug-ins, analytics, device abstraction layers, and deployment frameworks. Understand how the platform is designed for scalability, performance, and future-ready technology adoption.',
      sections: [
        {
          type: 'text',
          heading: 'Modular Architecture',
          paragraphs: [
            'Spatial Grid is designed as a modular platform, enabling enterprises to adopt capabilities incrementally rather than committing to a rigid, monolithic system. Organizations can start with specific modules aligned to immediate needs—such as training, visualization, or analytics—and expand over time as use cases grow.',
            'This modularity allows teams to integrate Spatial Grid into existing workflows and technology stacks without disruption. New features, integrations, or capabilities can be introduced without impacting deployed experiences, ensuring long-term flexibility and reduced risk as enterprise requirements evolve.',
          ],
        },
        {
          type: 'text',
          heading: 'Spatial Engines & Interaction Systems',
          paragraphs: [
            'At the core of Spatial Grid are spatial engines responsible for rendering, interaction logic, physics simulation, and environment behavior. These engines ensure that immersive experiences behave consistently and predictably across devices and environments.',
            'Interaction systems handle user inputs such as movement, gestures, gaze, and controller actions, translating them into meaningful interactions within the virtual or augmented environment. This enables natural, intuitive experiences that closely reflect real-world behavior, while maintaining high performance and stability.',
          ],
        },
        {
          type: 'text',
          heading: 'AI Plug-ins & Intelligence Layer',
          paragraphs: [
            'Spatial Grid incorporates an AI-driven intelligence layer that enhances immersion and adaptability. AI plug-ins support features such as avatar animation, procedural behaviors, guided workflows, and contextual responses based on user actions.',
            'These intelligent systems allow experiences to adapt dynamically—responding to user choices, performance, or environmental conditions. As a result, enterprises can create more personalized, engaging, and effective experiences that go beyond static content or linear interactions.',
          ],
        },
        {
          type: 'text',
          heading: 'Analytics & Data Layer',
          paragraphs: [
            'Spatial Grid includes a comprehensive analytics and data layer that captures meaningful interaction data across experiences. Metrics such as time spent, actions performed, completion rates, and engagement patterns are recorded to provide visibility into user behavior.',
            'These insights help enterprises evaluate effectiveness, identify improvement opportunities, and refine experience design. By connecting immersive experiences with measurable outcomes, organizations can align spatial initiatives with business, training, and operational goals.',
          ],
        },
        {
          type: 'text',
          heading: 'Scalability & Performance',
          paragraphs: [
            'Spatial Grid is optimized for enterprise-scale deployments, focusing on efficient memory usage, compute performance, and resource management. Experiences are designed to run smoothly across diverse hardware configurations without excessive system requirements.',
            'This performance-first approach enables organizations to scale deployments across locations, teams, and devices while maintaining stability and responsiveness. By minimizing hardware overhead and maximizing efficiency, Spatial Grid delivers high-quality immersive experiences with predictable operational costs.',
          ],
        },
      ],
    },
    3: {
      intro:
        'Spatial Grid supports multiple hardware types including AR/MR headsets, VR devices, mobiles, large-format displays, simulators, and sensors. This section explains device compatibility, supported configurations, and how experiences can be developed once and deployed across devices.',
      sections: [
        {
          type: 'text',
          heading: 'Supported Device Types',
          paragraphs: [
            'Spatial Grid supports a broad and diverse range of devices to meet enterprise deployment needs across industries and environments. This includes AR and MR headsets, VR devices, mobile phones and tablets, simulator cabins, large-format displays, projection-based systems, and sensor-driven setups.',
            'By supporting multiple device types within a single platform, Spatial Grid enables organizations to deploy immersive experiences across classrooms, factory floors, control rooms, training centers, retail spaces, and public installations—without fragmenting their technology stack.',
          ],
        },
        {
          type: 'text',
          heading: 'Device Abstraction Layer',
          paragraphs: [
            'At the core of Spatial Grid’s multi-device strategy is a device abstraction layer that separates experience logic from hardware-specific implementation. This allows the same experience to function consistently across different devices, regardless of form factor or input method.',
            'User interactions—such as gestures, controllers, gaze, touch, or motion—are mapped through a unified interaction framework. This ensures predictable behavior, reduced development complexity, and faster adaptation when introducing new hardware into existing deployments.',
          ],
        },
        {
          type: 'text',
          heading: 'Develop Once, Deploy Anywhere',
          paragraphs: [
            'Spatial Grid enables enterprises to design immersive experiences once and deploy them across multiple devices and environments. This approach eliminates the need to rebuild or duplicate experiences for each hardware type, significantly reducing development time and effort.',
            'As organizations expand deployments—from pilots to large-scale rollouts—the same experience can be reused, adapted, and updated centrally. This results in faster time to value, simplified maintenance, and a lower total cost of ownership over the lifecycle of the initiative.',
          ],
        },
        {
          type: 'text',
          heading: 'Hardware Flexibility & Future Readiness',
          paragraphs: [
            'Spatial Grid is built to remain adaptable as hardware ecosystems evolve. Enterprises are not locked into specific devices, vendors, or form factors, allowing them to adopt new technologies as they become available.',
            'This future-ready design protects long-term investments by ensuring existing experiences remain usable as hardware changes. Organizations gain the confidence to scale immersive initiatives knowing their platform can evolve alongside technological advancements and operational needs.',
          ],
        },
      ],
    },
    4: {
      intro:
        'Learn how enterprises design, configure, and manage immersive experiences using Spatial Grid. Covers experience workflows, content structuring, scene logic, interaction design, and how non-technical teams collaborate with developers and consultants.',
      sections: [
        {
          type: 'text',
          heading: 'Experience Design Workflow',
          paragraphs: [
            'Spatial Grid supports a structured, end-to-end workflow for designing immersive experiences that aligns with enterprise project lifecycles. Experiences typically begin with concept definition and requirement mapping, followed by storyboarding, interaction planning, and scenario design.',
            'This structured approach ensures that business objectives, learning outcomes, and operational requirements are clearly defined before development begins. As experiences move into implementation and deployment, teams maintain clarity, consistency, and traceability—reducing rework and ensuring predictable delivery.',
          ],
        },
        {
          type: 'text',
          heading: 'Content Structuring & Scene Logic',
          paragraphs: [
            'Experiences in Spatial Grid are organized into modular scenes, interactions, triggers, and behaviors. This clear structure allows complex scenarios to be broken down into manageable components that are easier to design, test, and maintain.',
            'Scene logic defines how users progress through experiences, how environments respond to actions, and how outcomes are triggered. This enables intuitive user journeys while supporting sophisticated workflows such as branching scenarios, conditional logic, and contextual feedback.',
          ],
        },
        {
          type: 'text',
          heading: 'Collaboration Across Teams',
          paragraphs: [
            'Spatial Grid is designed to support collaboration across diverse teams involved in enterprise projects. Subject matter experts contribute domain knowledge, non-technical stakeholders review and validate scenarios, and developers implement logic and interactions within a shared framework.',
            'Iterative review cycles allow teams to refine experiences based on feedback, ensuring accuracy, relevance, and alignment with real-world processes. This collaborative approach reduces communication gaps and results in experiences that are both technically robust and operationally accurate.',
          ],
        },
        {
          type: 'text',
          heading: 'Updates & Lifecycle Management',
          paragraphs: [
            'Spatial Grid enables enterprises to manage immersive experiences throughout their lifecycle without needing to rebuild them from scratch. Content updates, logic refinements, and experience enhancements can be introduced incrementally.',
            'This approach supports long-term use cases where procedures, regulations, or business requirements evolve over time. Enterprises can maintain relevance, scale deployments, and extend the lifespan of immersive experiences while minimizing downtime and maintenance effort.',
          ],
        },
      ],
    },
    5: {
      intro:
        'Understand how Spatial Grid supports enterprise-grade user management. Learn about roles, permissions, access control, and governance models to ensure secure usage across teams, departments, and locations.',
      sections: [
        {
          type: 'text',
          heading: 'Enterprise User Management',
          paragraphs: [
            'Spatial Grid supports structured user management designed for complex enterprise organizations. Users can be organized across multiple levels such as organizations, departments, teams, projects, and locations—reflecting real-world enterprise hierarchies.',
            'This structure allows administrators to manage access at scale while maintaining clarity and control. As organizations grow or reorganize, user access can be updated centrally without disrupting ongoing deployments or experiences.',
          ],
        },
        {
          type: 'text',
          heading: 'Roles & Permissions',
          paragraphs: [
            'Spatial Grid provides role-based access control that allows enterprises to clearly define what each user can view, edit, deploy, or manage. Common roles include administrators, creators, instructors, operators, and viewers, though roles can be tailored to specific organizational needs.',
            'By assigning permissions based on responsibility, enterprises reduce the risk of accidental changes, unauthorized access, or misuse of sensitive content. This ensures users interact only with the features and experiences relevant to their role.',
          ],
        },
        {
          type: 'text',
          heading: 'Access Control & Security',
          paragraphs: [
            'The platform includes granular access controls to safeguard content, data, and workflows across deployments. Permissions can be applied at experience, project, or environment levels—ensuring sensitive assets are accessible only to authorized users.',
            'These controls help protect intellectual property, operational procedures, and training content, particularly in regulated or high-risk industries. Spatial Grid’s security-first design supports enterprise expectations around confidentiality and controlled access.',
          ],
        },
        {
          type: 'text',
          heading: 'Governance Models',
          paragraphs: [
            'Spatial Grid supports governance frameworks that align with enterprise policies, audit processes, and compliance requirements. This includes support for approval workflows, access reviews, and controlled publishing of experiences.',
            'By enabling structured governance, organizations can ensure consistency, accountability, and traceability across immersive initiatives. This approach helps enterprises deploy spatial technologies confidently while meeting internal and external compliance standards.',
          ],
        },
      ],
    },
    6: {
      intro:
        'Discover how Spatial Grid captures interaction data, engagement metrics, and usage insights. Learn how enterprises use analytics to measure effectiveness, optimize experiences, and make informed decisions aligned with business and training outcomes.',
      sections: [
        {
          type: 'text',
          heading: 'Interaction & Engagement Metrics',
          paragraphs: [
            'Spatial Grid captures detailed interaction and engagement data across immersive experiences, providing visibility into how users navigate, interact, and respond within spatial environments. Metrics such as time spent, actions performed, paths taken, completion rates, and engagement levels are recorded at meaningful touchpoints.',
            'This granular data helps organizations understand user behavior beyond simple completion—revealing how individuals engage with content, where attention drops, and which interactions drive the most impact.',
          ],
        },
        {
          type: 'text',
          heading: 'Performance & Effectiveness Measurement',
          paragraphs: [
            'Enterprises use Spatial Grid analytics to evaluate the effectiveness of training, simulations, and engagement initiatives. By analyzing performance indicators such as task accuracy, scenario completion, and interaction patterns, organizations can identify skill gaps and learning challenges.',
            'These insights enable teams to validate whether immersive experiences are meeting intended outcomes and to refine scenarios to improve knowledge retention, confidence, and real-world performance.',
          ],
        },
        {
          type: 'text',
          heading: 'Reporting for Stakeholders',
          paragraphs: [
            'Spatial Grid provides actionable insights that can be shared across stakeholders, including business leaders, trainers, safety officers, and operations teams. Reports translate complex interaction data into clear summaries that support decision-making and strategic planning.',
            'By aligning analytics with business objectives, organizations can demonstrate the value of immersive initiatives, justify investments, and communicate progress across departments.',
          ],
        },
        {
          type: 'text',
          heading: 'Data-Driven Optimization',
          paragraphs: [
            'Analytics within Spatial Grid enable continuous, data-driven improvement of immersive experiences. Teams can iteratively refine content, interaction flows, and scenarios based on real usage patterns rather than assumptions.',
            'This ongoing optimization ensures experiences remain relevant as processes, regulations, and business needs evolve—allowing organizations to maintain effectiveness, scalability, and long-term value from their spatial initiatives.',
          ],
        },
      ],
    },
    7: {
      intro:
        'This section guides enterprises through deployment models, integration with existing systems, IT considerations, and operational readiness. Learn how Spatial Grid aligns with enterprise infrastructure, security standards, and compliance requirements.',
      sections: [
        {
          type: 'text',
          heading: 'Deployment Models',
          paragraphs: [
            'Spatial Grid supports multiple deployment models to align with diverse enterprise IT strategies and regulatory requirements. Organizations can deploy the platform in cloud, on-premise, hybrid, or tightly controlled environments depending on data sensitivity, operational constraints, and infrastructure preferences.',
            'This flexibility allows enterprises to adopt Spatial Grid without disrupting existing systems or policies. Deployments can evolve over time, enabling organizations to start with pilots and expand into broader rollouts as confidence and requirements grow.',
          ],
        },
        {
          type: 'text',
          heading: 'Integration with Enterprise Systems',
          paragraphs: [
            'Spatial Grid is designed to integrate seamlessly with existing enterprise systems and workflows. This includes learning management systems, asset databases, operational platforms, and other business applications where contextual data or reporting is required.',
            'By integrating with current systems, Spatial Grid ensures immersive experiences are not isolated initiatives but part of broader enterprise processes—supporting continuity, efficiency, and data consistency across the organization.',
          ],
        },
        {
          type: 'text',
          heading: 'IT & Security Alignment',
          paragraphs: [
            'The platform is built to meet enterprise IT expectations around security, scalability, and reliability. Spatial Grid supports secure access controls, data protection practices, and deployment architectures aligned with organizational security standards.',
            'This IT-first approach ensures that immersive initiatives can be adopted confidently, even in regulated or mission-critical environments. Spatial Grid aligns with compliance requirements and internal governance frameworks without compromising performance or usability.',
          ],
        },
        {
          type: 'text',
          heading: 'Operational Readiness',
          paragraphs: [
            'Spatial Grid provides guidance and best practices to ensure experiences are production-ready before large-scale deployment. This includes validation of performance, stability, and usability across devices and locations.',
            'Enterprises receive support to plan rollouts, manage updates, and monitor ongoing performance—ensuring immersive experiences remain reliable and scalable as they are adopted across teams and geographies.',
          ],
        },
      ],
    },
    8: {
      intro:
        'Learn how Spatial Grid supports enterprises beyond technology—through onboarding, training, consulting, and long-term partnerships. Understand engagement models, support structures, and how organizations scale their spatial initiatives with confidence.',
      sections: [
        {
          type: 'text',
          heading: 'Onboarding & Training',
          paragraphs: [
            'Spatial Grid offers structured onboarding and training programs designed to support diverse enterprise stakeholders, including business users, technical teams, instructors, and operational staff. These programs are tailored to align with organizational goals, use cases, and internal workflows.',
            'Training covers platform fundamentals, experience management, analytics interpretation, and operational best practices—ensuring teams are equipped to use, manage, and scale immersive experiences effectively. This approach reduces adoption friction and accelerates time to value.',
          ],
        },
        {
          type: 'text',
          heading: 'Consulting & Co-Creation',
          paragraphs: [
            'Spatial Grid engages with enterprises through consulting-led and co-creation models that go beyond technology delivery. Teams work closely with subject matter experts, domain consultants, and client stakeholders to ensure experiences reflect real-world processes and business objectives.',
            'This collaborative approach enables informed decision-making, accurate scenario design, and outcome-driven development. Enterprises benefit from guided expertise while retaining flexibility to evolve experiences as needs change.',
          ],
        },
        {
          type: 'text',
          heading: 'Support & Maintenance',
          paragraphs: [
            'Spatial Grid provides ongoing support to ensure platform stability, performance optimization, and smooth day-to-day operations. Support services address technical issues, system updates, and performance monitoring across deployments.',
            'By maintaining continuity and reliability, enterprises can focus on outcomes rather than platform maintenance. Support structures are designed to scale alongside deployments, ensuring consistent performance as usage grows.',
          ],
        },
        {
          type: 'text',
          heading: 'Scaling with Confidence',
          paragraphs: [
            'Spatial Grid partners with enterprises over the long term, supporting the journey from pilot projects to organization-wide deployments. This includes guidance on scaling strategies, governance, performance optimization, and change management.',
            'With a future-ready platform and a collaborative partnership model, organizations can expand immersive initiatives confidently—knowing their technology, processes, and support structures are built for sustained growth and impact.',
          ],
        },
      ],
    },
  };

  const currentContent = contentData[activeLeft] || contentData[0];

  return (
    <div className="text-[#FFFFFF]">
      <p className="font-light font-jakarta text-[15px] leading-relaxed mb-8">
        {currentContent.intro}
      </p>

      {currentContent.sections.map((section, index) => {
        // Use the same ID generation function
        const sectionId = generateSectionId(section.heading);

        return (
          <div key={index} className="mb-10">
            <h2
              id={sectionId}
              className="text-[22px] font-medium text-[#D8D8D8] mb-4 scroll-mt-28"
            >
              {section.heading}
            </h2>

            {/* 🔹 TEXT SECTION */}
            {section.type === 'text' &&
              section.paragraphs.map((paragraph, pIndex) => (
                <p
                  key={pIndex}
                  className="font-light text-[15px] leading-relaxed mb-4"
                >
                  {paragraph}
                </p>
              ))}

            {/* 🔹 LIST SECTION */}
            {section.type === 'list' && (
              <ul className="list-disc pl-6 space-y-3 text-[15px] font-light text-[#E4E4E4]">
                {section.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};
